import {
  ImageLoadObserver
} from "./chunk-YD36XMMR.js";
import {
  ImageUtils,
  createBlockImageViewElement,
  determineImageTypeForInsertionAtSelection,
  getImgViewElementMatcher,
  getSizeValueIfInPx,
  widthAndHeightStylesAreBothSet
} from "./chunk-SEDOTBEN.js";
import {
  ClipboardPipeline
} from "./chunk-DJCMRWX5.js";
import {
  Command,
  Plugin,
  UpcastWriter,
  first,
  logWarning,
  toArray
} from "./chunk-H3OD3CNY.js";

// node_modules/@ckeditor/ckeditor5-image/src/image/converters.js
function upcastImageFigure(imageUtils) {
  const converter = (evt, data, conversionApi) => {
    if (!conversionApi.consumable.test(data.viewItem, { name: true, classes: "image" })) {
      return;
    }
    const viewImage = imageUtils.findViewImgElement(data.viewItem);
    if (!viewImage || !conversionApi.consumable.test(viewImage, { name: true })) {
      return;
    }
    conversionApi.consumable.consume(data.viewItem, { name: true, classes: "image" });
    const conversionResult = conversionApi.convertItem(viewImage, data.modelCursor);
    const modelImage = first(conversionResult.modelRange.getItems());
    if (!modelImage) {
      conversionApi.consumable.revert(data.viewItem, { name: true, classes: "image" });
      return;
    }
    conversionApi.convertChildren(data.viewItem, modelImage);
    conversionApi.updateConversionResult(modelImage, data);
  };
  return (dispatcher) => {
    dispatcher.on("element:figure", converter);
  };
}
function downcastSrcsetAttribute(imageUtils, imageType) {
  const converter = (evt, data, conversionApi) => {
    if (!conversionApi.consumable.consume(data.item, evt.name)) {
      return;
    }
    const writer = conversionApi.writer;
    const element = conversionApi.mapper.toViewElement(data.item);
    const img = imageUtils.findViewImgElement(element);
    if (data.attributeNewValue === null) {
      writer.removeAttribute("srcset", img);
      writer.removeAttribute("sizes", img);
    } else {
      if (data.attributeNewValue) {
        writer.setAttribute("srcset", data.attributeNewValue, img);
        writer.setAttribute("sizes", "100vw", img);
      }
    }
  };
  return (dispatcher) => {
    dispatcher.on(`attribute:srcset:${imageType}`, converter);
  };
}
function downcastImageAttribute(imageUtils, imageType, attributeKey) {
  const converter = (evt, data, conversionApi) => {
    if (!conversionApi.consumable.consume(data.item, evt.name)) {
      return;
    }
    const viewWriter = conversionApi.writer;
    const element = conversionApi.mapper.toViewElement(data.item);
    const img = imageUtils.findViewImgElement(element);
    viewWriter.setAttribute(data.attributeKey, data.attributeNewValue || "", img);
  };
  return (dispatcher) => {
    dispatcher.on(`attribute:${attributeKey}:${imageType}`, converter);
  };
}

// node_modules/@ckeditor/ckeditor5-image/src/image/insertimagecommand.js
var InsertImageCommand = class extends Command {
  /**
   * @inheritDoc
   */
  constructor(editor) {
    super(editor);
    const configImageInsertType = editor.config.get("image.insert.type");
    if (!editor.plugins.has("ImageBlockEditing")) {
      if (configImageInsertType === "block") {
        logWarning("image-block-plugin-required");
      }
    }
    if (!editor.plugins.has("ImageInlineEditing")) {
      if (configImageInsertType === "inline") {
        logWarning("image-inline-plugin-required");
      }
    }
  }
  /**
   * @inheritDoc
   */
  refresh() {
    const imageUtils = this.editor.plugins.get("ImageUtils");
    this.isEnabled = imageUtils.isImageAllowed();
  }
  /**
   * Executes the command.
   *
   * @fires execute
   * @param options Options for the executed command.
   * @param options.source The image source or an array of image sources to insert.
   * See the documentation of the command to learn more about accepted formats.
   */
  execute(options) {
    const sourceDefinitions = toArray(options.source);
    const selection = this.editor.model.document.selection;
    const imageUtils = this.editor.plugins.get("ImageUtils");
    const selectionAttributes = Object.fromEntries(selection.getAttributes());
    sourceDefinitions.forEach((sourceDefinition, index) => {
      const selectedElement = selection.getSelectedElement();
      if (typeof sourceDefinition === "string") {
        sourceDefinition = { src: sourceDefinition };
      }
      if (index && selectedElement && imageUtils.isImage(selectedElement)) {
        const position = this.editor.model.createPositionAfter(selectedElement);
        imageUtils.insertImage({ ...sourceDefinition, ...selectionAttributes }, position);
      } else {
        imageUtils.insertImage({ ...sourceDefinition, ...selectionAttributes });
      }
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/image/replaceimagesourcecommand.js
var ReplaceImageSourceCommand = class extends Command {
  constructor(editor) {
    super(editor);
    this.decorate("cleanupImage");
  }
  /**
   * @inheritDoc
   */
  refresh() {
    const editor = this.editor;
    const imageUtils = editor.plugins.get("ImageUtils");
    const element = this.editor.model.document.selection.getSelectedElement();
    this.isEnabled = imageUtils.isImage(element);
    this.value = this.isEnabled ? element.getAttribute("src") : null;
  }
  /**
   * Executes the command.
   *
   * @fires execute
   * @param options Options for the executed command.
   * @param options.source The image source to replace.
   */
  execute(options) {
    const image = this.editor.model.document.selection.getSelectedElement();
    const imageUtils = this.editor.plugins.get("ImageUtils");
    this.editor.model.change((writer) => {
      writer.setAttribute("src", options.source, image);
      this.cleanupImage(writer, image);
      imageUtils.setImageNaturalSizeAttributes(image);
    });
  }
  /**
   * Cleanup image attributes that are not relevant to the new source.
   *
   * Removed attributes are: 'srcset', 'sizes', 'sources', 'width', 'height', 'alt'.
   *
   * This method is decorated, to allow custom cleanup logic.
   * For example, to remove 'myImageId' attribute after 'src' has changed:
   *
   * ```ts
   * replaceImageSourceCommand.on( 'cleanupImage', ( eventInfo, [ writer, image ] ) => {
   * 	writer.removeAttribute( 'myImageId', image );
   * } );
   * ```
   */
  cleanupImage(writer, image) {
    writer.removeAttribute("srcset", image);
    writer.removeAttribute("sizes", image);
    writer.removeAttribute("sources", image);
    writer.removeAttribute("width", image);
    writer.removeAttribute("height", image);
    writer.removeAttribute("alt", image);
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/image/imageediting.js
var ImageEditing = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageUtils];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageEditing";
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const conversion = editor.conversion;
    editor.editing.view.addObserver(ImageLoadObserver);
    conversion.for("upcast").attributeToAttribute({
      view: {
        name: "img",
        key: "alt"
      },
      model: "alt"
    }).attributeToAttribute({
      view: {
        name: "img",
        key: "srcset"
      },
      model: "srcset"
    });
    const insertImageCommand = new InsertImageCommand(editor);
    const replaceImageSourceCommand = new ReplaceImageSourceCommand(editor);
    editor.commands.add("insertImage", insertImageCommand);
    editor.commands.add("replaceImageSource", replaceImageSourceCommand);
    editor.commands.add("imageInsert", insertImageCommand);
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/imagesizeattributes.js
var ImageSizeAttributes = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageUtils];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageSizeAttributes";
  }
  /**
   * @inheritDoc
   */
  afterInit() {
    this._registerSchema();
    this._registerConverters("imageBlock");
    this._registerConverters("imageInline");
  }
  /**
   * Registers the `width` and `height` attributes for inline and block images.
   */
  _registerSchema() {
    if (this.editor.plugins.has("ImageBlockEditing")) {
      this.editor.model.schema.extend("imageBlock", { allowAttributes: ["width", "height"] });
    }
    if (this.editor.plugins.has("ImageInlineEditing")) {
      this.editor.model.schema.extend("imageInline", { allowAttributes: ["width", "height"] });
    }
  }
  /**
   * Registers converters for `width` and `height` attributes.
   */
  _registerConverters(imageType) {
    const editor = this.editor;
    const imageUtils = editor.plugins.get("ImageUtils");
    const viewElementName = imageType === "imageBlock" ? "figure" : "img";
    editor.conversion.for("upcast").attributeToAttribute({
      view: {
        name: viewElementName,
        styles: {
          width: /.+/
        }
      },
      model: {
        key: "width",
        value: (viewElement) => {
          if (widthAndHeightStylesAreBothSet(viewElement)) {
            return getSizeValueIfInPx(viewElement.getStyle("width"));
          }
          return null;
        }
      }
    }).attributeToAttribute({
      view: {
        name: viewElementName,
        key: "width"
      },
      model: "width"
    }).attributeToAttribute({
      view: {
        name: viewElementName,
        styles: {
          height: /.+/
        }
      },
      model: {
        key: "height",
        value: (viewElement) => {
          if (widthAndHeightStylesAreBothSet(viewElement)) {
            return getSizeValueIfInPx(viewElement.getStyle("height"));
          }
          return null;
        }
      }
    }).attributeToAttribute({
      view: {
        name: viewElementName,
        key: "height"
      },
      model: "height"
    });
    editor.conversion.for("editingDowncast").add((dispatcher) => {
      attachDowncastConverter(dispatcher, "width", "width", true);
      attachDowncastConverter(dispatcher, "height", "height", true);
    });
    editor.conversion.for("dataDowncast").add((dispatcher) => {
      attachDowncastConverter(dispatcher, "width", "width", false);
      attachDowncastConverter(dispatcher, "height", "height", false);
    });
    function attachDowncastConverter(dispatcher, modelAttributeName, viewAttributeName, setRatioForInlineImage) {
      dispatcher.on(`attribute:${modelAttributeName}:${imageType}`, (evt, data, conversionApi) => {
        if (!conversionApi.consumable.consume(data.item, evt.name)) {
          return;
        }
        const viewWriter = conversionApi.writer;
        const viewElement = conversionApi.mapper.toViewElement(data.item);
        const img = imageUtils.findViewImgElement(viewElement);
        if (data.attributeNewValue !== null) {
          viewWriter.setAttribute(viewAttributeName, data.attributeNewValue, img);
        } else {
          viewWriter.removeAttribute(viewAttributeName, img);
        }
        if (data.item.hasAttribute("sources")) {
          return;
        }
        const isResized = data.item.hasAttribute("resizedWidth");
        if (imageType === "imageInline" && !isResized && !setRatioForInlineImage) {
          return;
        }
        const width = data.item.getAttribute("width");
        const height = data.item.getAttribute("height");
        if (width && height) {
          viewWriter.setStyle("aspect-ratio", `${width}/${height}`, img);
        }
      });
    }
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/image/imagetypecommand.js
var ImageTypeCommand = class extends Command {
  /**
   * @inheritDoc
   *
   * @param modelElementName Model element name the command converts to.
   */
  constructor(editor, modelElementName) {
    super(editor);
    this._modelElementName = modelElementName;
  }
  /**
   * @inheritDoc
   */
  refresh() {
    const editor = this.editor;
    const imageUtils = editor.plugins.get("ImageUtils");
    const element = imageUtils.getClosestSelectedImageElement(this.editor.model.document.selection);
    if (this._modelElementName === "imageBlock") {
      this.isEnabled = imageUtils.isInlineImage(element);
    } else {
      this.isEnabled = imageUtils.isBlockImage(element);
    }
  }
  /**
   * Executes the command and changes the type of a selected image.
   *
   * @fires execute
   * @param options.setImageSizes Specifies whether the image `width` and `height` attributes should be set automatically.
   * The default is `true`.
   * @returns An object containing references to old and new model image elements
   * (for before and after the change) so external integrations can hook into the decorated
   * `execute` event and handle this change. `null` if the type change failed.
   */
  execute(options = {}) {
    const editor = this.editor;
    const model = this.editor.model;
    const imageUtils = editor.plugins.get("ImageUtils");
    const oldElement = imageUtils.getClosestSelectedImageElement(model.document.selection);
    const attributes = Object.fromEntries(oldElement.getAttributes());
    if (!attributes.src && !attributes.uploadId) {
      return null;
    }
    return model.change((writer) => {
      const { setImageSizes = true } = options;
      const markers = Array.from(model.markers).filter((marker) => marker.getRange().containsItem(oldElement));
      const newElement = imageUtils.insertImage(attributes, model.createSelection(oldElement, "on"), this._modelElementName, { setImageSizes });
      if (!newElement) {
        return null;
      }
      const newElementRange = writer.createRangeOn(newElement);
      for (const marker of markers) {
        const markerRange = marker.getRange();
        const range = markerRange.root.rootName != "$graveyard" ? markerRange.getJoined(newElementRange, true) : newElementRange;
        writer.updateMarker(marker, { range });
      }
      return {
        oldElement,
        newElement
      };
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/image/imageplaceholder.js
import "/tmp/build_5d4c22f1/node_modules/@ckeditor/ckeditor5-image/theme/imageplaceholder.css";
var ImagePlaceholder = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageUtils];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImagePlaceholder";
  }
  /**
   * @inheritDoc
   */
  afterInit() {
    this._setupSchema();
    this._setupConversion();
    this._setupLoadListener();
  }
  /**
   * Extends model schema.
   */
  _setupSchema() {
    const schema = this.editor.model.schema;
    if (schema.isRegistered("imageBlock")) {
      schema.extend("imageBlock", {
        allowAttributes: ["placeholder"]
      });
    }
    if (schema.isRegistered("imageInline")) {
      schema.extend("imageInline", {
        allowAttributes: ["placeholder"]
      });
    }
  }
  /**
   * Registers converters.
   */
  _setupConversion() {
    const editor = this.editor;
    const conversion = editor.conversion;
    const imageUtils = editor.plugins.get("ImageUtils");
    conversion.for("editingDowncast").add((dispatcher) => {
      dispatcher.on("attribute:placeholder", (evt, data, conversionApi) => {
        if (!conversionApi.consumable.test(data.item, evt.name)) {
          return;
        }
        if (!data.item.is("element", "imageBlock") && !data.item.is("element", "imageInline")) {
          return;
        }
        conversionApi.consumable.consume(data.item, evt.name);
        const viewWriter = conversionApi.writer;
        const element = conversionApi.mapper.toViewElement(data.item);
        const img = imageUtils.findViewImgElement(element);
        if (data.attributeNewValue) {
          viewWriter.addClass("image_placeholder", img);
          viewWriter.setStyle("background-image", `url(${data.attributeNewValue})`, img);
          viewWriter.setCustomProperty("editingPipeline:doNotReuseOnce", true, img);
        } else {
          viewWriter.removeClass("image_placeholder", img);
          viewWriter.removeStyle("background-image", img);
        }
      });
    });
  }
  /**
   * Prepares listener for image load.
   */
  _setupLoadListener() {
    const editor = this.editor;
    const model = editor.model;
    const editing = editor.editing;
    const editingView = editing.view;
    const imageUtils = editor.plugins.get("ImageUtils");
    editingView.addObserver(ImageLoadObserver);
    this.listenTo(editingView.document, "imageLoaded", (evt, domEvent) => {
      const imgViewElement = editingView.domConverter.mapDomToView(domEvent.target);
      if (!imgViewElement) {
        return;
      }
      const viewElement = imageUtils.getImageWidgetFromImageView(imgViewElement);
      if (!viewElement) {
        return;
      }
      const modelElement = editing.mapper.toModelElement(viewElement);
      if (!modelElement || !modelElement.hasAttribute("placeholder")) {
        return;
      }
      model.enqueueChange({ isUndoable: false }, (writer) => {
        writer.removeAttribute("placeholder", modelElement);
      });
    });
  }
};

// node_modules/@ckeditor/ckeditor5-image/src/image/imageblockediting.js
var ImageBlockEditing = class extends Plugin {
  /**
   * @inheritDoc
   */
  static get requires() {
    return [ImageEditing, ImageSizeAttributes, ImageUtils, ImagePlaceholder, ClipboardPipeline];
  }
  /**
   * @inheritDoc
   */
  static get pluginName() {
    return "ImageBlockEditing";
  }
  /**
   * @inheritDoc
   */
  init() {
    const editor = this.editor;
    const schema = editor.model.schema;
    schema.register("imageBlock", {
      inheritAllFrom: "$blockObject",
      allowAttributes: ["alt", "src", "srcset"]
    });
    this._setupConversion();
    if (editor.plugins.has("ImageInlineEditing")) {
      editor.commands.add("imageTypeBlock", new ImageTypeCommand(this.editor, "imageBlock"));
      this._setupClipboardIntegration();
    }
  }
  /**
   * Configures conversion pipelines to support upcasting and downcasting
   * block images (block image widgets) and their attributes.
   */
  _setupConversion() {
    const editor = this.editor;
    const t = editor.t;
    const conversion = editor.conversion;
    const imageUtils = editor.plugins.get("ImageUtils");
    conversion.for("dataDowncast").elementToStructure({
      model: "imageBlock",
      view: (modelElement, { writer }) => createBlockImageViewElement(writer)
    });
    conversion.for("editingDowncast").elementToStructure({
      model: "imageBlock",
      view: (modelElement, { writer }) => imageUtils.toImageWidget(createBlockImageViewElement(writer), writer, t("image widget"))
    });
    conversion.for("downcast").add(downcastImageAttribute(imageUtils, "imageBlock", "src")).add(downcastImageAttribute(imageUtils, "imageBlock", "alt")).add(downcastSrcsetAttribute(imageUtils, "imageBlock"));
    conversion.for("upcast").elementToElement({
      view: getImgViewElementMatcher(editor, "imageBlock"),
      model: (viewImage, { writer }) => writer.createElement("imageBlock", viewImage.hasAttribute("src") ? { src: viewImage.getAttribute("src") } : void 0)
    }).add(upcastImageFigure(imageUtils));
  }
  /**
   * Integrates the plugin with the clipboard pipeline.
   *
   * Idea is that the feature should recognize the user's intent when an **inline** image is
   * pasted or dropped. If such an image is pasted/dropped:
   *
   * * into an empty block (e.g. an empty paragraph),
   * * on another object (e.g. some block widget).
   *
   * it gets converted into a block image on the fly. We assume this is the user's intent
   * if they decided to put their image there.
   *
   * See the `ImageInlineEditing` for the similar integration that works in the opposite direction.
   *
   * The feature also sets image `width` and `height` attributes on paste.
   */
  _setupClipboardIntegration() {
    const editor = this.editor;
    const model = editor.model;
    const editingView = editor.editing.view;
    const imageUtils = editor.plugins.get("ImageUtils");
    const clipboardPipeline = editor.plugins.get("ClipboardPipeline");
    this.listenTo(clipboardPipeline, "inputTransformation", (evt, data) => {
      const docFragmentChildren = Array.from(data.content.getChildren());
      let modelRange;
      if (!docFragmentChildren.every(imageUtils.isInlineImageView)) {
        return;
      }
      if (data.targetRanges) {
        modelRange = editor.editing.mapper.toModelRange(data.targetRanges[0]);
      } else {
        modelRange = model.document.selection.getFirstRange();
      }
      const selection = model.createSelection(modelRange);
      if (determineImageTypeForInsertionAtSelection(model.schema, selection) === "imageBlock") {
        const writer = new UpcastWriter(editingView.document);
        const blockViewImages = docFragmentChildren.map((inlineViewImage) => writer.createElement("figure", { class: "image" }, inlineViewImage));
        data.content = writer.createDocumentFragment(blockViewImages);
      }
    });
    this.listenTo(clipboardPipeline, "contentInsertion", (evt, data) => {
      if (data.method !== "paste") {
        return;
      }
      model.change((writer) => {
        const range = writer.createRangeIn(data.content);
        for (const item of range.getItems()) {
          if (item.is("element", "imageBlock")) {
            imageUtils.setImageNaturalSizeAttributes(item);
          }
        }
      });
    });
  }
};

export {
  downcastSrcsetAttribute,
  downcastImageAttribute,
  ImageEditing,
  ImageSizeAttributes,
  ImageTypeCommand,
  ImagePlaceholder,
  ImageBlockEditing
};
/*! Bundled license information:

@ckeditor/ckeditor5-image/src/image/converters.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/image/insertimagecommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/image/replaceimagesourcecommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/image/imageediting.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/imagesizeattributes.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/image/imagetypecommand.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/image/imageplaceholder.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)

@ckeditor/ckeditor5-image/src/image/imageblockediting.js:
  (**
   * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
   * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
   *)
*/
//# sourceMappingURL=chunk-5DBMGC34.js.map
